/**
 * `wayai template` — consumer surface for the Templates gallery.
 *
 *   wayai template list            list the joint template set
 *   wayai template pull <slug>     write the wayai slice into wayai-ws/hubs/<slug>/
 *
 * Pull writes only the WAYAI slice; if the channel runs on a Rekor backend (`uses`),
 * it prints a cross-link hint (`rekor template pull <backend>`). Authoring lives in
 * the private wayai-admin skill (`wayai admin template push`).
 */

import { requireAuth } from '../lib/auth.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { findGitRoot } from '../lib/workspace.js';
import { resolveLayout } from '../lib/layout.js';
import { writeFileMap, detectCollisions } from '../lib/template-files.js';
import { TEMPLATE_SLUG_REGEX, templateLocaleSchema, distinctUsesBackends } from '@wayai/core/contracts';
import type { TemplateListItem, TemplateLocale } from '@wayai/core/contracts';
import * as path from 'node:path';

export async function templateCommand(args: string[]): Promise<void> {
  const [sub, ...rest] = args;
  switch (sub) {
    case 'list':
      await runTemplateList();
      return;
    case 'pull':
      await runTemplatePull(rest);
      return;
    case undefined:
    case 'help':
    case '--help':
    case '-h':
      printTemplateHelp();
      return;
    default:
      console.error(`Unknown template subcommand: ${sub}`);
      printTemplateHelp();
      process.exit(1);
  }
}

function includesLabel(item: Pick<TemplateListItem, 'includes' | 'uses'>): string {
  const parts: string[] = [];
  if (item.includes.wayai) parts.push('wayai agent');
  // `uses` may be absent when reading a catalog from a backend that predates it.
  if (item.uses?.length) parts.push('rekor backend');
  return parts.length ? parts.join(' + ') : '(none)';
}

async function runTemplateList(): Promise<void> {
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.templateList();
  } catch (err) {
    exitOnTemplateApiError(err);
    throw err; // unreachable
  }

  const { templates } = response;
  if (templates.length === 0) {
    console.log('(no templates published yet)');
    return;
  }

  const slugWidth = Math.max(4, ...templates.map((t) => t.slug.length));
  const catWidth = Math.max(8, ...templates.map((t) => t.category.length));
  console.log(`${'SLUG'.padEnd(slugWidth)}  ${'CATEGORY'.padEnd(catWidth)}  INCLUDES`);
  for (const t of templates) {
    console.log(`${t.slug.padEnd(slugWidth)}  ${t.category.padEnd(catWidth)}  ${includesLabel(t)}`);
  }
}

async function runTemplatePull(rest: string[]): Promise<void> {
  // Minimal inline flag parse: `--lang <locale>` (value), `--force`/`--dry-run` (boolean).
  const positional: string[] = [];
  let lang: TemplateLocale | undefined;
  let force = false;
  let dryRun = false;
  for (let i = 0; i < rest.length; i++) {
    if (rest[i] === '--force') { force = true; continue; }
    if (rest[i] === '--dry-run') { dryRun = true; continue; }
    if (rest[i] === '--lang') {
      const v = rest[++i];
      if (!v) { console.error('--lang requires a value'); process.exit(1); }
      const parsed = templateLocaleSchema.safeParse(v);
      if (!parsed.success) {
        console.error(`Invalid --lang "${v}": must be one of ${templateLocaleSchema.options.join(', ')}.`);
        process.exit(1);
      }
      lang = parsed.data;
      continue;
    }
    if (rest[i].startsWith('--')) { console.error(`Unknown flag: ${rest[i]}`); process.exit(1); }
    positional.push(rest[i]);
  }

  const slug = positional[0];
  if (!slug) {
    console.error('wayai template pull <slug> [--lang <locale>]');
    process.exit(1);
  }
  // Validate locally before the slug is ever used to build a filesystem path
  // (the server also gates it; this closes the client-side traversal gap).
  if (!TEMPLATE_SLUG_REGEX.test(slug)) {
    console.error(`Invalid template slug: ${slug}`);
    process.exit(1);
  }

  const gitRoot = findGitRoot();
  if (!gitRoot) {
    console.error('Not in a git repository. Run `wayai template pull` inside your project repo.');
    process.exit(1);
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let detail;
  try {
    // Omit --lang → the server resolves to the template's defaultLocale.
    detail = await client.templatePull(slug, lang);
  } catch (err) {
    exitOnTemplateApiError(err);
    throw err; // unreachable
  }

  // The server falls back to the template's defaultLocale when the requested
  // locale isn't translated yet — surface that so the user knows what they got.
  if (detail.resolvedLocale !== detail.requestedLocale) {
    console.log(`Note: "${detail.requestedLocale}" not translated yet — pulled "${detail.resolvedLocale}" instead.`);
  }

  // Degenerate: a published template with no WayAI slice — nothing to write into a
  // WayAI workspace. Point at its backend(s) if it declares any.
  if (!detail.includes.wayai) {
    const backends = distinctUsesBackends(detail.uses);
    const hint = backends.length
      ? ` — run ${backends.map((b) => `\`rekor template pull ${b}${langFlag(lang)}\``).join(' and ')} for its data layer.`
      : '.';
    console.log(`Template "${slug}" has no WayAI slice${hint}`);
    return;
  }

  const targetDir = path.join(resolveLayout(gitRoot).hubsDir, slug);
  const relTarget = path.relative(gitRoot, targetDir);

  // Guard the footgun: a re-pull truncate-overwrites colliding files (e.g. an edited
  // hub.yaml). Refuse if any collide unless --force; --dry-run previews without writing.
  // detectCollisions validates every path, so the dry-run preview can iterate keys directly.
  const collisions = detectCollisions(targetDir, detail.files);

  if (dryRun) {
    const planned = Object.keys(detail.files).sort((a, b) => a.localeCompare(b));
    const collisionSet = new Set(collisions);
    console.log(`Dry run — would write ${planned.length} file${planned.length === 1 ? '' : 's'} into ${relTarget}/:`);
    for (const rel of planned) {
      console.log(`  ${collisionSet.has(rel) ? 'overwrite' : 'create'}  ${rel}`);
    }
    if (collisions.length > 0) {
      console.log(`\n${collisions.length} existing file${collisions.length === 1 ? '' : 's'} would be overwritten — re-run with --force to proceed.`);
    }
    return;
  }

  if (collisions.length > 0 && !force) {
    console.error(`Refusing to overwrite ${collisions.length} existing file${collisions.length === 1 ? '' : 's'} in ${relTarget}/:`);
    for (const rel of collisions) console.error(`  ${rel}`);
    console.error(`\nCommit or move your changes and re-run with --force to overwrite, or pull into a fresh slug dir.`);
    console.error(`Preview the full file map first with --dry-run.`);
    process.exit(1);
  }

  const written = writeFileMap(targetDir, detail.files);
  console.log(`Pulled template "${slug}" → ${relTarget}/ (${written.length} file${written.length === 1 ? '' : 's'}).`);
  console.log(`Next: \`wayai push\` to deploy it into your own hub.`);

  const backends = distinctUsesBackends(detail.uses);
  if (backends.length) {
    const cmds = backends.map((b) => `\`rekor template pull ${b}${langFlag(lang)}\``).join(' and ');
    const noun = backends.length === 1 ? 'a Rekor backend' : 'Rekor backends';
    const obj = backends.length === 1 ? 'it' : 'them';
    console.log(`\nThis channel runs on ${noun} — run ${cmds} to add ${obj}.`);
  }
}

/** Carry the user's `--lang` choice into a cross-product hint (empty when omitted). */
function langFlag(lang: TemplateLocale | undefined): string {
  return lang ? ` --lang ${lang}` : '';
}

function printTemplateHelp(): void {
  console.log(`wayai template — install ready-made hub templates

Usage:
  wayai template list                                       List available templates
  wayai template pull <slug> [--lang <l>] [--force] [--dry-run]
                                                            Write a template's WayAI slice into wayai-ws/hubs/<slug>/

--lang selects a localized variant (${templateLocaleSchema.options.join('|')}); omitted = the template's default
language. An untranslated language falls back to the default (with a note).
--dry-run previews the file map (marking files that would be overwritten) without writing.
A pull refuses to overwrite existing files in the target dir; pass --force to overwrite them.

After pulling, run \`wayai push\` to deploy the template into your own hub.`);
}

function exitOnTemplateApiError(err: unknown): never {
  if (err instanceof ApiError) {
    if (err.status === 401) {
      console.error('Authentication failed. Run `wayai login` again.');
    } else if (err.status === 404) {
      console.error('Template not found. Run `wayai template list` to see available templates.');
    } else if (err.status === 429) {
      console.error('Rate limited. Please retry in a moment.');
    } else {
      console.error(err.message);
    }
    process.exit(1);
  }
  console.error(err instanceof Error ? err.message : String(err));
  process.exit(1);
}
